<template>
	<div>
		<v-dialog 
			scrollable
			transition="dialog-bottom-transition"
			:value="true"
			max-width="680px"
			@click:outside="handleClose"
			@keydown.esc="handleClose"
		>
			<v-card :dark="isDark">
				<v-card-title class="pa-0">
					<v-toolbar
						:color="isDark ? 'grey darken-3' : 'grey lighten-3'"
						:dark="isDark"
						flat
						dense
					>
						<v-toolbar-title>{{ candidate.name }}</v-toolbar-title>
						<v-spacer></v-spacer>
					</v-toolbar>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text style="padding: 0">
					<AutofillCandidateItem
						requestMethod="put"
						:fileId="parseAndAutofillFileId"
						:jobId="null"
						:candidateProps="candidate"
						ref="edit_candidate_item"
					></AutofillCandidateItem>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="handleClose">{{ $t('button.close') }}</v-btn>
					<v-btn @click="handleSubmit" color="primary" dark>
						{{ $t('button.apply') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-snackbar
			v-model="snackbar.isVisible"
			:timeout="snackbar.timeout"
			left
			bottom
			color="error"
			:dark="isDark"
		>
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text icon v-bind="attrs" @click="snackbar.isVisible = false">
					<v-icon>close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import AutofillCandidateItem from '../autofill/autofillCandidateItem/AutofillCandidateItem';

export default {
	name: 'EditCandidateProps',
	components: {
		AutofillCandidateItem,
	},
	computed: {
		...mapState({
			isDark: state => state.currentUser.isDark,
		}),
	},
	props: {
		candidate: {
			type: Object,
			required: true,
		},
		onClose: {
			type: Function,
			required: true,
		},
		parseAndAutofillFileId: {
			type: [Number, String, Object],
			required: false,
		},
	},
	data() {
		return {
			snackbar: {
				isVisible: false,
				timeout: 5000,
				text: '',
			},
		};
	},
	methods: {
		scrollDialogToTop() {
			this.$nextTick(() => {
				const scrollEl = this.$el.closest('.v-dialog')?.querySelector('.v-dialog__content');
				if (scrollEl) {
					scrollEl.scrollTop = 0;
				}
			});
		},
		getInvalidFieldLabels(errors) {
			const labelKeys = {
				full_name: 'label.full-name',
				name: 'label.full-name',
				identification_number: 'label.tckn',
				birth_date: 'label.birth-date',
				current_company: 'label.current-company',
				country_code: 'label.country',
				city: 'label.city',
				county: 'label.county',
				last_school: 'label.last-school',
				total_years_experience: 'label.total-years-experience',
				linkedin_profile: 'label.linkedin-profile',
				secretcv_profile: 'label.secretcv-profile',
				source: 'label.source',
				tags: 'label.tags',
			};
			const fieldNames = Object.keys(errors || {}).filter(
				key => Array.isArray(errors[key]) && errors[key].length > 0
			);
			const labels = fieldNames.map((key) => {
				if (/^_vee_\d+$/.test(key)) return null;
				if (labelKeys[key]) return this.$t(labelKeys[key]);
				if (/^email_\d+$/.test(key)) return this.$t('label.email');
				if (/^phone_\d+$/.test(key)) return this.$t('label.phone');
				if (/^education_\d+_/.test(key)) return this.$t('label.education');
				if (/^work_experience_\d+_/.test(key)) return this.$t('label.work-experience');
				return key;
			}).filter(Boolean);
			return [...new Set(labels)];
		},
		async handleSubmit(evt) {
			if (evt) {
				evt.preventDefault();
			}

			const isValid = await this.$refs.edit_candidate_item.handleValidate();

			if (!isValid) {
				await this.$nextTick();
				const errors = this.$refs.edit_candidate_item.getValidationErrors();
				const labels = this.getInvalidFieldLabels(errors);
				const baseMessage = this.$t('snackbar-messages.please-fix-form-errors');
				const detail = labels.length > 0
					? `: ${labels.join(', ')}`
					: '';
				this.snackbar = {
					isVisible: true,
					timeout: 7000,
					text: baseMessage + detail,
				};
				this.scrollDialogToTop();
				return;
			}

			const {
					name,
					current_company,
					country_code,
					city,
					county,
					identification_number,
					birth_date,
					last_school,
					linkedin_profile,
          			secretcv_profile,
					email,
					phone,
					link_questions,
					tags,
					source,
					total_years_experience,
					work_experience,
					education,
				} = this.$refs.edit_candidate_item.handleGetCandidateItem();

				const formatDate = (dateStr) => {
					if (dateStr === 'null') return null;
					
					if (!dateStr) return dateStr;

					if (/^\d{4}$/.test(dateStr)) {
						return `01.01.${dateStr}`;
					}

					if (/^\d{2}\.\d{4}$/.test(dateStr)) {
						return `01.${dateStr}`;
					}

					return dateStr;
				};

				work_experience.forEach(experience => {
					experience.start_date = formatDate(experience.start_date);
					experience.end_date = formatDate(experience.end_date);
				});

				education.forEach(education => {
					education.start_date = formatDate(education.start_date);
					education.end_date = formatDate(education.end_date);
				});

			this.$store
				.dispatch('candidates/put', {
					items: [
						{
							id: this.candidate.id,
							name,
							current_company,
							country_code,
							city,
							county,
							identification_number,
							birth_date,
							last_school,
							linkedin_profile,
							secretcv_profile,
							email,
							phone,
							link_questions,
							tags,
							source,
							total_years_experience,
							work_experience,
							education,
						},
					],
				})
				.then(() => {
					this.$store.commit('candidates/setItemsNeedToRefresh');
					this.onClose({ needsRefresh: true });
				})
				.catch((error) => {
					const message =
						error?.response?.data?.data?.message ||
						error?.response?.data?.message ||
						this.$t('snackbar-messages.an-error-occurred');
					this.snackbar = {
						isVisible: true,
						timeout: 5000,
						text: typeof message === 'string' ? message : this.$t('snackbar-messages.an-error-occurred'),
					};
					this.scrollDialogToTop();
				});
		},
		handleClose() {
			this.onClose({ needsRefresh: false });
		},
	},
};
</script>
